import { getAuth } from 'firebase/auth';
import { type ItemListType } from '../../../types/common';
import { captureAsyncError } from '../../../utils/errorHandler';

// Storageにアップロード済みファイルのパス情報
export interface FilePathInfo {
  filePath: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  pageCount: number;
}

// 読取パターン用OCR処理のリクエスト型
export interface OcrTextPatternRequest {
  filePaths: FilePathInfo[];
  patternId: string;
  itemList: ItemListType[];
  resultIds: string[];
  processingType?: number;
  geminiModel?: number;
  // Gemini 2.5用: 思考バジェット（0=無効, -1=自動, 1024〜24576=トークン上限）
  thinkingBudget?: number | null;
  // Gemini 3用: 思考レベル（minimal, low, medium, high）
  thinkingLevel?: string | null;
  // OCRエンジン切替（#2960 運営スタッフ限定・ocrEngineType の数値。未指定は Vision）
  ocrEngine?: number;
  // Geminiサンプリングパラメータ（#2960 運営スタッフ限定・1回目OCR抽出用。未指定はモデルデフォルト）
  geminiTemperature?: number | null;
  geminiTopP?: number | null;
  // 行の矩形座標を Gemini に渡すか（#2960 方式B・運営スタッフ限定）
  includeCoordinates?: boolean;
  // 2回目構造化エンジン（#2960 structuredEngineType・運営スタッフ限定。未指定は gemini）
  structuredEngine?: number;
  // 代理操作関連
  isProxy?: boolean;
  operatorId?: string;
  operatorRole?: number;
  // 代理ログイン時の代理先企業ID（フリーOCRはパターン実体が無く会社IDを引けないため必須）
  proxyCompanyId?: string;
  // FE側で仮引き済みフラグ（trueの場合、Functions側のincrementProvisionalOcrUsageをスキップ）
  provisionalDeducted?: boolean;
  // FE側で仮引きしたOCRクレジット額（エラー時のロールバック用）
  feProvisionalCreditAmount?: number;
  // 仮加算（current_month_ocr_cost）に +15円 が乗ったか。BEの確定 delta 計算に渡す。
  provisionalHasMasterMapping?: boolean;
  // クレジット仮引きで超過側として扱った結果数。BEのクレジット消費対象タスク数逆算に使う（#2941）。
  provisionalExcessResultCount?: number;
  // クレジット仮引きで超過側として扱ったページ数。BE singleResult の超過ページ判定に使う（#2942）。
  provisionalExcessPages?: number;
}

// 読取パターン用OCR処理のレスポンス型
export interface OcrTextPatternResponse {
  taskCount: number;
  message: string;
}

/**
 * 読取パターン用AI読取処理を実行
 * Storageにアップロード済みファイルのパス情報をJSON送信してCloud Tasksに登録する
 * @param request - リクエストデータ
 * @returns Cloud Tasks登録結果
 */
export const handleOcrTextPattern = captureAsyncError(
  async (request: OcrTextPatternRequest): Promise<OcrTextPatternResponse> => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('ユーザー認証が必要です');
      }

      // Firebase認証トークンを取得
      const idToken = await user.getIdToken();

      // Cloud Functionsエンドポイントを呼び出し
      const functionUrl = process.env.REACT_APP_FUNCTION_URL;

      if (!functionUrl) {
        throw new Error('REACT_APP_FUNCTION_URL environment variable is not set');
      }

      // JSONリクエストボディを構築
      const body: Record<string, unknown> = {
        filePaths: request.filePaths,
        patternId: request.patternId,
        itemList: request.itemList,
        resultIds: request.resultIds,
      };

      if (request.processingType !== undefined) {
        body.processingType = request.processingType;
      }
      if (request.geminiModel !== undefined) {
        body.geminiModel = request.geminiModel;
      }
      if (request.thinkingBudget !== undefined && request.thinkingBudget !== null) {
        body.thinkingBudget = request.thinkingBudget;
      }
      if (request.thinkingLevel !== undefined && request.thinkingLevel !== null) {
        body.thinkingLevel = request.thinkingLevel;
      }
      if (request.ocrEngine !== undefined) {
        body.ocrEngine = request.ocrEngine;
      }
      if (request.geminiTemperature !== undefined && request.geminiTemperature !== null) {
        body.geminiTemperature = request.geminiTemperature;
      }
      if (request.geminiTopP !== undefined && request.geminiTopP !== null) {
        body.geminiTopP = request.geminiTopP;
      }
      if (request.includeCoordinates) {
        body.includeCoordinates = true;
      }
      if (request.structuredEngine !== undefined) {
        body.structuredEngine = request.structuredEngine;
      }
      if (request.provisionalDeducted) {
        body.provisionalDeducted = true;
      }
      if (
        request.feProvisionalCreditAmount !== undefined &&
        request.feProvisionalCreditAmount > 0
      ) {
        body.feProvisionalCreditAmount = request.feProvisionalCreditAmount;
      }
      if (request.provisionalHasMasterMapping) {
        body.provisionalHasMasterMapping = true;
      }
      if (
        request.provisionalExcessResultCount !== undefined &&
        request.provisionalExcessResultCount > 0
      ) {
        body.provisionalExcessResultCount = request.provisionalExcessResultCount;
      }
      if (request.provisionalExcessPages !== undefined && request.provisionalExcessPages > 0) {
        body.provisionalExcessPages = request.provisionalExcessPages;
      }
      if (request.isProxy) {
        body.isProxy = true;
        if (request.operatorId) {
          body.operatorId = request.operatorId;
        }
        if (request.operatorRole !== undefined) {
          body.operatorRole = request.operatorRole;
        }
        if (request.proxyCompanyId) {
          body.proxyCompanyId = request.proxyCompanyId;
        }
      }

      // getOcrTextPatternエンドポイントを呼び出し（JSON送信）
      const response = await fetch(`${functionUrl}/getOcrTextPattern`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // エラー内容をSentryのために取得
        await response.json().catch(async () => {
          await response.text();
        });
        throw new Error('AI読取処理に失敗しました。もう一度お試しください。');
      }

      const result = await response.json();
      return result as OcrTextPatternResponse;
    } catch (error) {
      // エラーがすでにErrorオブジェクトの場合はそのまま、そうでなければ新しいErrorを作成
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('AI読取処理に失敗しました。もう一度お試しください。');
    }
  },
);
